import { Controller } from "@hotwired/stimulus"

// Renders a small palette of section-type buttons. Two modes (set via
// the Stimulus `mode` value):
//
//  - "add"          : POSTs a new slide on the body row. The originating
//                     add-section zone's `afterSlideId` is sent on the
//                     POST body so the server can insert the new slide
//                     at the clicked position (instead of always
//                     appending). An empty string means "insert at the
//                     top"; the server resolves it to a 0-indexed
//                     `slide_order_position` against the row's live
//                     ranked order.
//                     Mounted by InnerBodyAdminChromeHelper#inner_body_add_section_zone
//                     directly onto each add-section zone in the inline
//                     edit view.
//
//  - "change-type"  : PATCHes an existing slide's section_type. Mounted
//                     dynamically by admin_block_controller#openTypePalette
//                     when the admin clicks the "Change type" toolbar
//                     button.
//
// Allowed section types come from the `palette` value as a comma-
// separated list. URLs are passed in as values (patchUrl / createUrl)
// so the controller never constructs route paths from string templates.
//
// After a successful request, reload the page via Turbo so the rendered
// inner body reflects the new/changed slide. We clear the Turbo cache
// first to defeat snapshot reuse of the pre-change DOM.
export default class extends Controller {
  static values = {
    mode: { type: String, default: "add" },
    palette: String,
    slideId: String,
    // afterSlideId: identifies which add-section zone fired the request.
    // Sent on the POST so the server inserts the new slide at the
    // clicked position. Empty string ("") means "insert at the top".
    afterSlideId: String,
    rowId: String,
    currentType: String,
    patchUrl: String,
    createUrl: String
  }

  connect() {
    // "change-type" is opened via admin_block_controller#openTypePalette
    // and should expand immediately — the admin already clicked a
    // button to get here. "add" mode is mounted inline on every zone
    // between sections; it must stay collapsed (the server-rendered
    // "+ add section" text inside the dashed wrapper) until the admin
    // chooses to open it. Without this guard, pages with N existing
    // sections render N+1 always-on palette popovers stacked down
    // the page.
    this._expanded = this.modeValue === "change-type"
    if (this._expanded) this._renderExpanded()
    // collapsed: leave the server-rendered "+ add section" text alone;
    // the wrapper itself carries data-action=click->...#toggle so a
    // click anywhere inside the dashed zone triggers expand.
  }

  toggle(event) {
    // When the palette is already expanded, clicks bubble up from each
    // palette button — those have their own action (pick), so don't
    // treat them as a re-collapse. Only toggle when the click target is
    // the dashed wrapper itself (not one of its descendants).
    if (this._expanded && event && event.target !== this.element) return
    if (event) event.preventDefault()
    this._expanded = !this._expanded
    if (this._expanded) {
      this._renderExpanded()
    } else {
      this.element.textContent = "+ add section"
    }
  }

  _renderExpanded() {
    const palette = (this.paletteValue || "").split(",").filter(Boolean)
    const current = this.currentTypeValue || ""
    const labels = {
      body: "Body",
      pull_quote: "Pull quote",
      figure: "Figure",
      section_break: "Section break",
      staff_card: "Staff card",
      stat: "Stat",
      testimonial: "Testimonial",
      hero: "Hero",
      signoff: "Closing",
      cta: "CTA"
    }
    const heading = this.modeValue === "change-type" ? "Change section type" : "Add a section"
    this.element.innerHTML = `
      <div class="sh-admin-palette">
        <div class="sh-admin-palette-label">${heading}</div>
        <ul>
          ${palette.map((t) => `
            <li>
              <button type="button" data-action="click->admin-section-palette#pick" data-type="${t}" class="${t === current ? "is-current" : ""}">
                ${labels[t] || t}
              </button>
            </li>
          `).join("")}
        </ul>
      </div>
    `
  }

  // In "add" mode the originating zone's `afterSlideId` is sent on the
  // POST body so the server inserts the new slide at the clicked
  // position (instead of always appending at the end of the row).
  // Empty string is the explicit "insert at top" sentinel; the server
  // resolves it to slide_order_position 0 against the row's live
  // ranked order.
  async pick(event) {
    const button = event.currentTarget
    // Debounce: a fast double-click would otherwise fire pick() twice
    // and create (or change) two slides server-side. Disable the
    // button until the request resolves; reset on failure so the user
    // can retry. On success the page reloads via Turbo.visit anyway.
    if (button.disabled) return
    button.disabled = true
    const type = button.dataset.type
    const csrfMeta = document.querySelector("meta[name=csrf-token]")
    const csrf = csrfMeta?.content
    if (!csrf) { button.disabled = false; return }

    let res
    if (this.modeValue === "change-type") {
      res = await fetch(this.patchUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf,
          Accept: "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify({ slide: { section_type: type } })
      })
    } else {
      res = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf,
          Accept: "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify({
          slide: {
            section_type: type,
            content: "",
            after_slide_id: this.afterSlideIdValue || ""
          }
        })
      })
    }

    if (!res.ok) {
      button.disabled = false
      alert("Couldn't apply that section type.")
      return
    }
    if (window.Turbo) {
      window.Turbo.cache.clear()
      window.Turbo.visit(window.location.href, { action: "replace" })
    } else {
      window.location.reload()
    }
  }
}
